import type { AssetRecord } from '../../shared/document';

export const MAX_IMAGE_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_IMAGE_BATCH_BYTES = 256 * 1024 * 1024;
export const MAX_IMAGE_PIXELS = 50_000_000;
const THUMBNAIL_EDGE = 320;

const MIME_BY_EXTENSION: Record<string, AssetRecord['mime']> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

export type PreparedImage = {
  asset: AssetRecord;
  blob: Blob;
  thumbnailUrl: string;
};

export function getSupportedImageMime(file: Pick<File, 'name' | 'type'>): AssetRecord['mime'] | null {
  const extension = file.name.split('.').at(-1)?.toLowerCase() ?? '';
  const extensionMime = MIME_BY_EXTENSION[extension];
  if (!extensionMime) return null;
  if (file.type && file.type !== extensionMime) return null;
  return extensionMime;
}

function sanitizeSvg(xml: string, name: string): { blob: Blob; width: number; height: number } {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) throw new Error(`${name}: SVG z DTD ali zunanjimi entitetami ni podprt.`);
  const parsed = new DOMParser().parseFromString(xml, 'image/svg+xml');
  const root = parsed.documentElement;
  if (parsed.querySelector('parsererror') || root.localName.toLowerCase() !== 'svg') throw new Error(`${name}: datoteka ni veljaven SVG.`);
  const hasUnsafeUrl = (value: string): boolean => Array.from(value.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi)).some((match) => !match[2]?.trim().startsWith('#'));
  const dimension = (value: string | null): number | null => {
    if (!value || value.trim().endsWith('%')) return null;
    const match = value.trim().match(/^([\d.]+)\s*(px|pt|pc|mm|cm|in)?$/i);
    if (!match) return null;
    const units: Record<string, number> = { px: 1, pt: 96 / 72, pc: 16, mm: 96 / 25.4, cm: 96 / 2.54, in: 96 };
    return Number(match[1]) * (units[match[2]?.toLowerCase() ?? 'px'] ?? 1);
  };
  const viewBox = (root.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
  const validViewBox = viewBox.length === 4 && viewBox.every(Number.isFinite) && viewBox[2]! > 0 && viewBox[3]! > 0;
  const width = dimension(root.getAttribute('width')) ?? (validViewBox ? viewBox[2]! : 300);
  const height = dimension(root.getAttribute('height')) ?? (validViewBox ? viewBox[3]! : 150);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 || width * height > MAX_IMAGE_PIXELS) throw new Error(`${name}: mere SVG-ja niso veljavne ali presegajo omejitev 50 milijonov slikovnih pik.`);
  for (const node of [root, ...Array.from(root.querySelectorAll('*'))]) {
    if (['script', 'foreignobject', 'iframe', 'object', 'embed'].includes(node.localName.toLowerCase())) throw new Error(`${name}: SVG vsebuje aktivno ali zunanjo vsebino.`);
    for (const attribute of Array.from(node.attributes)) {
      const attrName = attribute.localName.toLowerCase();
      const value = attribute.value.trim();
      if (attrName.startsWith('on') || ((attrName === 'href' || attrName === 'src') && value && !value.startsWith('#'))) throw new Error(`${name}: SVG vsebuje skripto ali zunanjo povezavo.`);
      if (hasUnsafeUrl(value) || /@import|javascript\s*:/i.test(value)) throw new Error(`${name}: SVG vsebuje nevaren slog ali zunanji vir.`);
    }
  }
  for (const style of Array.from(root.querySelectorAll('style'))) {
    const css = style.textContent ?? '';
    if (hasUnsafeUrl(css) || /@import|javascript\s*:/i.test(css)) throw new Error(`${name}: SVG vsebuje zunanji vir v slogu.`);
  }
  const blob = new Blob([new XMLSerializer().serializeToString(root)], { type: 'image/svg+xml' });
  if (blob.size > MAX_IMAGE_FILE_BYTES) throw new Error(`${name}: očiščen SVG presega omejitev 50 MiB.`);
  return { blob, width: Math.ceil(width), height: Math.ceil(height) };
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Slike ni bilo mogoče pripraviti za urejanje.'));
    }, mime, mime === 'image/jpeg' || mime === 'image/webp' ? 0.96 : undefined);
  });
}

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  const mime = getSupportedImageMime(file);
  if (!mime) throw new Error(`${file.name}: podprti so JPEG, PNG, WebP in SVG.`);
  if (file.size === 0) throw new Error(`${file.name}: datoteka je prazna.`);
  if (file.size > MAX_IMAGE_FILE_BYTES) throw new Error(`${file.name}: datoteka presega omejitev 50 MiB.`);

  if (mime === 'image/svg+xml') {
    let svg: ReturnType<typeof sanitizeSvg>;
    try { svg = sanitizeSvg(await file.text(), file.name); }
    catch (error) {
      if (error instanceof Error && error.message.startsWith(`${file.name}:`)) throw error;
      throw new Error(`${file.name}: SVG ni mogoče varno prebrati.`);
    }
    let bitmap: ImageBitmap;
    try { bitmap = await createImageBitmap(svg.blob); }
    catch { throw new Error(`${file.name}: SVG ni mogoče prikazati. Preverite, da datoteka ni poškodovana.`); }
    try {
      if (bitmap.width * bitmap.height > MAX_IMAGE_PIXELS) throw new Error(`${file.name}: SVG presega omejitev 50 milijonov slikovnih pik.`);
      const id = await sha256(svg.blob);
      const asset: AssetRecord = { id, path: `assets/${id}.svg`, originalName: file.name, mime, width: bitmap.width, height: bitmap.height, sha256: id };
      const scale = Math.min(1, THUMBNAIL_EDGE / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error(`${file.name}: sličice ni mogoče pripraviti.`);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const thumbnail = await canvasToBlob(canvas, 'image/jpeg');
      return { asset, blob: svg.blob, thumbnailUrl: URL.createObjectURL(thumbnail) };
    } finally { bitmap.close(); }
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error(`${file.name}: slike ni mogoče dekodirati. Preverite, da datoteka ni poškodovana.`);
  }

  try {
    const width = bitmap.width;
    const height = bitmap.height;
    if (width <= 0 || height <= 0 || width * height > MAX_IMAGE_PIXELS) {
      throw new Error(`${file.name}: slika presega omejitev 50 milijonov slikovnih pik.`);
    }

    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = width;
    sourceCanvas.height = height;
    const sourceContext = sourceCanvas.getContext('2d');
    if (!sourceContext) throw new Error(`${file.name}: grafični kontekst ni na voljo.`);
    sourceContext.drawImage(bitmap, 0, 0);
    const normalized = await canvasToBlob(sourceCanvas, mime);
    if (normalized.size > MAX_IMAGE_FILE_BYTES) {
      throw new Error(`${file.name}: normalizirana slika presega omejitev 50 MiB.`);
    }

    const id = await sha256(normalized);
    const outputMime = normalized.type as AssetRecord['mime'];
    const extension = outputMime === 'image/jpeg' ? 'jpg' : outputMime === 'image/png' ? 'png' : 'webp';
    const asset: AssetRecord = {
      id,
      path: `assets/${id}.${extension}`,
      originalName: file.name,
      mime: outputMime,
      width,
      height,
      sha256: id,
    };

    const thumbnailBitmap = await createImageBitmap(normalized);
    try {
      const scale = Math.min(1, THUMBNAIL_EDGE / Math.max(width, height));
      const thumbnailCanvas = document.createElement('canvas');
      thumbnailCanvas.width = Math.max(1, Math.round(width * scale));
      thumbnailCanvas.height = Math.max(1, Math.round(height * scale));
      const thumbnailContext = thumbnailCanvas.getContext('2d');
      if (!thumbnailContext) throw new Error(`${file.name}: sličice ni mogoče pripraviti.`);
      thumbnailContext.drawImage(thumbnailBitmap, 0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
      const thumbnail = await canvasToBlob(thumbnailCanvas, 'image/jpeg');
      return { asset, blob: normalized, thumbnailUrl: URL.createObjectURL(thumbnail) };
    } finally {
      thumbnailBitmap.close();
    }
  } finally {
    bitmap.close();
  }
}
